import React from "react";
import styled, { keyframes } from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import airtelMoneyImage from "../assets/img/airtel_money.png";
import MtnMoMoImage from "../assets/img/momo.jpg";
import visaImage from "../assets/img/visaPng.png";
import masterCardImage from "../assets/img/mastercardPng.png";
import aramexImage from "../assets/img/amex.png";
import codImage from "../assets/img/cod.png";
import PaymentIcon from "@material-ui/icons/Payment";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import SecurityIcon from "@material-ui/icons/Security";
import LockIcon from "@material-ui/icons/Lock";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import SpeedIcon from "@material-ui/icons/Speed";
import { COUNTRY_NAME } from "../config/market";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #324D54 0%, #1a2a2e 50%, #0f1a1c 100%);
  padding: var(--lusuku-nav-h, 120px) 20px 140px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: #f8f9fa;
    border-radius: 100% 100% 0 0;
    transform: translateY(50%);
  }

  @media (max-width: 768px) {
    padding: var(--lusuku-nav-h, 140px) 16px 120px;
    
    &::after {
      height: 50px;
    }
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const HeroIcon = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(255, 193, 7, 0.15);
  border: 2px solid rgba(255, 193, 7, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  animation: ${float} 3s ease-in-out infinite;

  svg {
    font-size: 40px;
    color: #FFC107;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.2rem;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ContentSection = styled.section`
  padding: 60px 20px 80px;
  
  @media (max-width: 768px) {
    padding: 40px 16px 60px;
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 16px;

  span {
    color: #fc5c65;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SectionSubtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 48px;
  line-height: 1.7;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 32px;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 60px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e8e8e8;
`;

const CategoryIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${props => props.$bg || 'linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)'};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 26px;
    color: white;
  }
`;

const CategoryTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`;

const PaymentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PaymentCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  display: flex;
  gap: 20px;
  align-items: flex-start;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-fill-mode: both;
  animation-delay: ${props => props.$delay || '0s'};
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.$accent || '#324D54'};
  }

  @media (max-width: 768px) {
    padding: 20px;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const PaymentLogo = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 16px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid #e8e8e8;

  img {
    width: 60px;
    height: 60px;
    object-fit: contain;
  }
`;

const PaymentInfo = styled.div`
  flex: 1;
`;

const PaymentName = styled.h4`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const PaymentDescription = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 12px;
`;

const PaymentFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FeatureTag = styled.span`
  background: ${props => props.$bg || '#f0f0f0'};
  color: ${props => props.$color || '#555'};
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    font-size: 12px;
  }
`;

const SecuritySection = styled.div`
  background: linear-gradient(135deg, #324D54 0%, #1a2a2e 100%);
  border-radius: 20px;
  padding: 48px;
  color: white;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const SecurityTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  svg {
    color: #FFC107;
    font-size: 32px;
  }
`;

const SecurityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
`;

const SecurityCard = styled.div`
  text-align: center;
`;

const SecurityIconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;

  svg {
    font-size: 28px;
    color: #FFC107;
  }
`;

const SecurityLabel = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const SecurityText = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  margin: 0;
`;

const HowItWorksSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 48px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const StepCard = styled.div`
  text-align: center;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 30px;
    right: -16px;
    width: 32px;
    height: 2px;
    background: linear-gradient(90deg, #fc5c65, transparent);

    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #fc5c65 0%, #eb3b5a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
`;

const StepTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const StepText = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0;
`;

const mobileMoneyOptions = [
  {
    name: "MTN Mobile Money",
    image: MtnMoMoImage,
    description: `${COUNTRY_NAME}'s mobile money service. Pay instantly with your MTN MoMo account.`,
    accent: "#FFCC00",
    features: ["Instant Payment", "No Hidden Fees", "24/7 Available"]
  },
  {
    name: "Airtel Money",
    image: airtelMoneyImage,
    description: "Fast and secure payments with Airtel Money. Simple, convenient, and reliable.",
    accent: "#ED1C24",
    features: ["Quick Transfer", "Low Charges", "Nationwide"]
  }
];

const cardOptions = [
  {
    name: "Visa",
    image: visaImage,
    description: "Shop with confidence using Visa's globally trusted and secure payment network.",
    accent: "#1A1F71",
    features: ["Global Acceptance", "Fraud Protection", "Secure"]
  },
  {
    name: "MasterCard",
    image: masterCardImage,
    description: "Flexible and secure payment solutions accepted worldwide for all your needs.",
    accent: "#EB001B",
    features: ["Worldwide", "Secure Checkout", "Rewards"]
  },
  {
    name: "American Express",
    image: aramexImage,
    description: "Premium payment experience with enhanced security and exclusive benefits.",
    accent: "#006FCF",
    features: ["Premium Service", "Buyer Protection", "Rewards"]
  }
];

const otherOptions = [
  {
    name: "Cash On Delivery",
    image: codImage,
    description: "Pay when your order arrives. Inspect your items before making payment.",
    accent: "#10B981",
    features: ["Pay on Arrival", "Inspect First", "No Prepayment"]
  }
];

const PaymentOptions = () => {
  return (
    <PageWrapper>
      <Navbar />
      
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroIcon>
            <PaymentIcon />
          </HeroIcon>
          <HeroTitle>Payment Options</HeroTitle>
          <HeroSubtitle>
            We offer multiple secure payment methods for your convenience. 
            Choose the option that works best for you and shop with confidence.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {/* Content Section */}
      <ContentSection>
        <Container>
          <SectionTitle>Accepted <span>Payment Methods</span></SectionTitle>
          <SectionSubtitle>
            From mobile money to international cards, we've got you covered with 
            safe and convenient payment options.
          </SectionSubtitle>

          {/* Mobile Money */}
          <CategorySection>
            <CategoryHeader>
              <CategoryIcon $bg="linear-gradient(135deg, #FFC107 0%, #FF9800 100%)">
                <PhoneAndroidIcon />
              </CategoryIcon>
              <CategoryTitle>Mobile Money</CategoryTitle>
            </CategoryHeader>
            <PaymentGrid>
              {mobileMoneyOptions.map((option, index) => (
                <PaymentCard key={option.name} $delay={`${0.1 + index * 0.1}s`} $accent={option.accent}>
                  <PaymentLogo>
                    <img src={option.image} alt={option.name} />
                  </PaymentLogo>
                  <PaymentInfo>
                    <PaymentName>{option.name}</PaymentName>
                    <PaymentDescription>{option.description}</PaymentDescription>
                    <PaymentFeatures>
                      {option.features.map((feature, i) => (
                        <FeatureTag key={i} $bg="rgba(255, 193, 7, 0.1)" $color="#b38600">
                          <CheckCircleIcon /> {feature}
                        </FeatureTag>
                      ))}
                    </PaymentFeatures>
                  </PaymentInfo>
                </PaymentCard>
              ))}
            </PaymentGrid>
          </CategorySection>

          {/* Credit/Debit Cards */}
          <CategorySection>
            <CategoryHeader>
              <CategoryIcon $bg="linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)">
                <CreditCardIcon />
              </CategoryIcon>
              <CategoryTitle>Credit & Debit Cards</CategoryTitle>
            </CategoryHeader>
            <PaymentGrid>
              {cardOptions.map((option, index) => (
                <PaymentCard key={option.name} $delay={`${0.1 + index * 0.1}s`} $accent={option.accent}>
                  <PaymentLogo>
                    <img src={option.image} alt={option.name} />
                  </PaymentLogo>
                  <PaymentInfo>
                    <PaymentName>{option.name}</PaymentName>
                    <PaymentDescription>{option.description}</PaymentDescription>
                    <PaymentFeatures>
                      {option.features.map((feature, i) => (
                        <FeatureTag key={i} $bg="rgba(99, 102, 241, 0.1)" $color="#4F46E5">
                          <CheckCircleIcon /> {feature}
                        </FeatureTag>
                      ))}
                    </PaymentFeatures>
                  </PaymentInfo>
                </PaymentCard>
              ))}
            </PaymentGrid>
          </CategorySection>

          {/* Other Payment Methods */}
          <CategorySection>
            <CategoryHeader>
              <CategoryIcon $bg="linear-gradient(135deg, #10B981 0%, #059669 100%)">
                <LocalAtmIcon />
              </CategoryIcon>
              <CategoryTitle>Other Payment Methods</CategoryTitle>
            </CategoryHeader>
            <PaymentGrid>
              {otherOptions.map((option, index) => (
                <PaymentCard key={option.name} $delay={`${0.1 + index * 0.1}s`} $accent={option.accent}>
                  <PaymentLogo>
                    <img src={option.image} alt={option.name} />
                  </PaymentLogo>
                  <PaymentInfo>
                    <PaymentName>{option.name}</PaymentName>
                    <PaymentDescription>{option.description}</PaymentDescription>
                    <PaymentFeatures>
                      {option.features.map((feature, i) => (
                        <FeatureTag key={i} $bg="rgba(16, 185, 129, 0.1)" $color="#059669">
                          <CheckCircleIcon /> {feature}
                        </FeatureTag>
                      ))}
                    </PaymentFeatures>
                  </PaymentInfo>
                </PaymentCard>
              ))}
            </PaymentGrid>
          </CategorySection>

          {/* Security Section */}
          <SecuritySection>
            <SecurityTitle>
              <SecurityIcon /> Your Security is Our Priority
            </SecurityTitle>
            <SecurityGrid>
              <SecurityCard>
                <SecurityIconWrapper>
                  <LockIcon />
                </SecurityIconWrapper>
                <SecurityLabel>SSL Encryption</SecurityLabel>
                <SecurityText>All transactions are encrypted with 256-bit SSL security</SecurityText>
              </SecurityCard>
              <SecurityCard>
                <SecurityIconWrapper>
                  <VerifiedUserIcon />
                </SecurityIconWrapper>
                <SecurityLabel>PCI Compliant</SecurityLabel>
                <SecurityText>We follow strict PCI DSS standards for card security</SecurityText>
              </SecurityCard>
              <SecurityCard>
                <SecurityIconWrapper>
                  <SecurityIcon />
                </SecurityIconWrapper>
                <SecurityLabel>Fraud Protection</SecurityLabel>
                <SecurityText>Advanced fraud detection to protect your payments</SecurityText>
              </SecurityCard>
              <SecurityCard>
                <SecurityIconWrapper>
                  <SpeedIcon />
                </SecurityIconWrapper>
                <SecurityLabel>Instant Verification</SecurityLabel>
                <SecurityText>Real-time payment verification for quick checkout</SecurityText>
              </SecurityCard>
            </SecurityGrid>
          </SecuritySection>

          {/* How It Works */}
          <HowItWorksSection>
            <SectionTitle>How Payment <span>Works</span></SectionTitle>
            <StepsGrid>
              <StepCard>
                <StepNumber>1</StepNumber>
                <StepTitle>Add to Cart</StepTitle>
                <StepText>Browse products and add your favorites to the shopping cart</StepText>
              </StepCard>
              <StepCard>
                <StepNumber>2</StepNumber>
                <StepTitle>Checkout</StepTitle>
                <StepText>Review your order and proceed to the secure checkout page</StepText>
              </StepCard>
              <StepCard>
                <StepNumber>3</StepNumber>
                <StepTitle>Choose Payment</StepTitle>
                <StepText>Select your preferred payment method from our options</StepText>
              </StepCard>
              <StepCard>
                <StepNumber>4</StepNumber>
                <StepTitle>Confirm & Pay</StepTitle>
                <StepText>Complete your payment securely and receive confirmation</StepText>
              </StepCard>
            </StepsGrid>
          </HowItWorksSection>

        </Container>
      </ContentSection>

      <Footer />
    </PageWrapper>
  );
};

export default PaymentOptions;
